// Imports
import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components/macro';
import { StepNumbers } from '../../app';
import { fieldContainerBottomMargin } from '../../../shareables/foundation/front-end/components/forms/field-container';
import Input from '../../../shareables/foundation/front-end/components/forms/fields/input';


// Styled components
interface WrapperProps {
	displayVerificationCode: boolean;
}

const Wrapper = styled.div<WrapperProps>`
	${({ displayVerificationCode }) =>
		displayVerificationCode
			? css`
					text-align: center;
					margin-bottom: ${fieldContainerBottomMargin};
					
					label {
						font-weight: 400;
						font-size: 1.4rem;
						margin-bottom: 1.25rem;
						padding-bottom: 0;
						
						> small {
							display: block;
							font-style: normal;
							font-size: 0.875rem;
							margin-top: 0.62rem;
							margin-left: 0;
							
							> strong {
								display: inline-block;
								word-break: break-all;
								width: max-content;
								max-width: 100%;
							}
						}
					}
					
					input {
						text-align: center;
					}
				`
			: css`
					display: none;
				`}
`;


// Define the accepted props
interface Props {
	stepNumber: StepNumbers;
	emailGetter: React.MutableRefObject<(() => string) | undefined>;
	verificationCodeSetter: React.MutableRefObject<((newValue: string) => void) | undefined>;
	verificationCodeExpiredError: string;
	setVerificationCodeExpiredError: React.Dispatch<React.SetStateAction<string>>;
	formRef: React.RefObject<HTMLFormElement>;
}


// Function component
const VerificationCode: React.FC<Props> = ({
	stepNumber,
	emailGetter,
	verificationCodeSetter,
	verificationCodeExpiredError,
	setVerificationCodeExpiredError,
	formRef,
}) => {
	// Use ref
	const changesSinceVerificationCodeExpiredError = useRef(false);
	const previousValue = useRef('');
	
	
	// Reset previous value on unmount
	useEffect(() => {
		return () => {
			previousValue.current = '';
		};
	}, []);
	
	
	// Handle changes
	const handleChange: React.ComponentProps<typeof Input>['onChange'] = (newValue: string) => {
		// Handle clearing verification code expired error when needed
		if (verificationCodeExpiredError) {
			// The first change will be when we clear the field, so we should detect when any change has already been made
			if (changesSinceVerificationCodeExpiredError.current) {
				setVerificationCodeExpiredError('');
				changesSinceVerificationCodeExpiredError.current = false;
			} else {
				changesSinceVerificationCodeExpiredError.current = true;
			}
		}
		
		
		// Auto submit form if six characters are pasted in and pass validations
		if (previousValue.current.length === 0 && newValue.length === 6) {
			formRef.current?.requestSubmit?.(); // LATER: When browser support for `requestSubmit` is better we can remove the optional chaining
		}
		
		previousValue.current = newValue;
	};
	
	
	// Return JSX
	return (
		<Wrapper displayVerificationCode={stepNumber === 1}>
			<Input
				name='verificationCode'
				type={stepNumber === 1 ? 'text' : 'hidden'}
				inputMode='numeric'
				required
				autoFocus={stepNumber === 1}
				placeholder='6-digit code'
				label='Verify your email address'
				note={
					<React.Fragment>
						Enter the 6-digit code we just sent to <strong>{emailGetter.current?.()}</strong> to continue.
					</React.Fragment>
				}
				minLength={6}
				maxLength={6}
				pattern='[0-9]*'
				title='Must be a 6-digit code'
				recoverState={{
					enabled: false,
					uniqueIdentifier: 'will-not-recover-state',
				}}
				errorMessage={verificationCodeExpiredError || undefined}
				bindSetter={(setter) => (verificationCodeSetter.current = setter)}
				onChange={handleChange}
			/>
		</Wrapper>
	);
};

export default VerificationCode;
